import { HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";

import * as Sentry from "@sentry/angular";

import { IConfig, IUserData } from "@shared/interfaces";
import { APP_CONFIG } from "@shared/tokens";

import { FeatureFlagService } from "./feature-flag.service";

@Injectable({
  providedIn: "root",
})
export class ErrorReportingService {
  constructor(
    @Inject(APP_CONFIG) private environment: IConfig,
    private featureFlagService: FeatureFlagService,
  ) {}

  captureException(err: any) {
    if (!this.environment.sentryDsn) {
      console.log(err);

      return;
    }

    const errorToReport = this.getError(err);

    Sentry.captureException(errorToReport, {
      extra: this.extractExtra(err),
    });
  }

  public setUserContext(user: IUserData | null): void {
    if (user) {
      const { activeOrganisationIndex, availableOrganisations } = user;
      const activeOrganisation = availableOrganisations[activeOrganisationIndex];

      Sentry.setUser({
        id: user.id,
        email: user.email,
        type: user.type,
        activeOrganisationId: activeOrganisation?.id,
        activeOrganisationName: activeOrganisation?.name,
        roles: activeOrganisation?.roles?.length ? activeOrganisation.roles.join(", ") : undefined,
        featureFlags: this.featureFlagService.getAllFlagsCurrentValues(),
      });
    } else {
      Sentry.setUser(null);
    }
  }

  private getError(err: any): Error | HttpErrorResponse {
    if (err instanceof Error) {
      return err;
    }
    const newErr = new Error(err.message || "unexpected");

    for (const [key, value] of Object.entries(err)) {
      newErr[key] = value;
    }

    return newErr;
  }

  /**
   * Extracts additional context from different types of errors,
   * especially HttpErrorResponse instances, to be sent to Sentry
   * as part of the "extra" field.
   *
   * This helps provide more actionable insights in error reports,
   * including HTTP status, response body, and headers.
   *
   * @param err - The original error object, which may be of any type
   * @returns An object with additional contextual data for error reporting
   */
  private extractExtra(err: any): Record<string, any> {
    if (err instanceof HttpErrorResponse) {
      return {
        status: err.status,
        statusText: err.statusText,
        url: err.url,
        message: err.message,
        errorBody: err.error,
        headers: err.headers?.keys()?.reduce(
          (acc, key) => {
            acc[key] = err.headers?.get(key);

            return acc;
          },
          {} as Record<string, string>,
        ),
      };
    }

    // For generic errors or non-HttpErrorResponse cases,
    // include all enumerable properties except "stack"
    return Object.fromEntries(Object.entries(err).filter(([key]) => key !== "stack"));
  }
}
